import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Check, Copy, CreditCard, Plus, Sparkles, Trash2 } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api, APIError, extractErrorMessage } from '../../api/client'
import { useB2BBillingStore } from '../../stores/useB2BBillingStore'

const QUESTION_CATEGORIES = [
  { id: 'hard_skills', label: 'Hard skills' },
  { id: 'soft_skills', label: 'Soft skills' },
  { id: 'behavioral',  label: 'Behavioral' },
  { id: 'motivation',  label: 'Motivation' },
]

// Mirrors the language list in JobConstructorScreen so recruiters see the
// same options in both places.
const SCREENING_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Russian' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'pl', label: 'Polish' },
  { code: 'de', label: 'German' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
]

const languageLabel = (code) =>
  SCREENING_LANGUAGES.find((l) => l.code === code)?.label || (code || '').toUpperCase()

/**
 * Phase 1 screening editor: ручная вводка вопросов + генерация уник-ссылки.
 * AI-генерация вопросов — Phase 2.
 */
export default function ScreeningEditorScreen() {
  const { jobId, candidateId } = useParams()
  const [job, setJob] = useState(null)
  const [candidate, setCandidate] = useState(null)
  const [screening, setScreening] = useState(null)
  const [questions, setQuestions] = useState([{ id: tempId(), text: '' }])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [quotaError, setQuotaError] = useState('')
  const [copied, setCopied] = useState(false)
  const [aiCategories, setAiCategories] = useState(['hard_skills', 'behavioral'])
  const [aiCount, setAiCount] = useState(8)
  const [genStage, setGenStage] = useState(null)   // null | 'planning' | 'writing'
  const [genElapsed, setGenElapsed] = useState(0)  // seconds since generation started
  const aiGenerating = genStage !== null
  const [aiError, setAiError] = useState('')
  // Per-screening AI language. Initialized from existing screening or job
  // default; recruiter can change before generation (in-memory) or after
  // (PATCH /language).
  const [screeningLanguage, setScreeningLanguage] = useState('en')
  const [languageSaving, setLanguageSaving] = useState(false)
  const loadSubscription = useB2BBillingStore((s) => s.loadSubscription)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get(`/api/b2b/jobs/${jobId}`),
      api.get(`/api/b2b/candidates/${candidateId}`),
      api.get(`/api/b2b/screenings/by-pair/${jobId}/${candidateId}`),
    ])
      .then(([jobData, candData, scrData]) => {
        if (cancelled) return
        setJob(jobData)
        setCandidate(candData)
        // Default the language selector to the existing screening's language,
        // or fall back to the job-level default for first-time screenings.
        setScreeningLanguage(scrData?.language || jobData?.language || 'en')
        if (scrData) {
          setScreening(scrData)
          if (scrData.questions?.length) {
            setQuestions(scrData.questions.map((q) => ({
              id: q.id,
              text: q.text,
              category: q.category || null,
              rationale: q.rationale || null,
              expected_signal: q.expected_signal || null,
            })))
          }
        }
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof APIError && err.status === 404) setError('Job or candidate not found.')
        else setError('Could not load screening.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [jobId, candidateId])

  // Elapsed-seconds ticker for the generation progress indicator.
  useEffect(() => {
    if (genStage === null) { setGenElapsed(0); return }
    const startedAt = Date.now()
    setGenElapsed(0)
    const t = setInterval(() => setGenElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000)
    return () => clearInterval(t)
  }, [genStage])

  const isCompleted = screening?.status === 'completed'

  const updateQuestion = (id, text) => {
    setQuestions((prev) => prev.map((q) => q.id === id ? { ...q, text } : q))
  }

  const addQuestion = () => {
    setQuestions((prev) => [...prev, { id: tempId(), text: '' }])
  }

  const removeQuestion = (id) => {
    setQuestions((prev) => prev.length > 1 ? prev.filter((q) => q.id !== id) : prev)
  }

  const toggleCategory = (cat) => {
    setAiCategories((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat])
  }

  const handleLanguageChange = async (next) => {
    const prev = screeningLanguage
    setScreeningLanguage(next)
    setAiError('')
    // If screening doesn't exist yet, the selected language will be applied
    // when the first POST /api/b2b/screenings happens (handleGenerateAI / handleSave).
    if (!screening?.id || next === prev) return
    setLanguageSaving(true)
    try {
      const updated = await api.patch(`/api/b2b/screenings/${screening.id}/language`, {
        language: next,
      })
      setScreening(updated)
    } catch (err) {
      setScreeningLanguage(prev)
      const message = err instanceof APIError ? extractErrorMessage(err.data) : err.message
      setAiError(message || 'Could not change screening language.')
    } finally {
      setLanguageSaving(false)
    }
  }

  const handleGenerateAI = async () => {
    setAiError('')

    // Resolve the screening id locally — React state is async, so reading
    // `screening?.id` right after `setScreening(stub)` returns the stale value.
    let activeScreening = screening
    if (!activeScreening?.id) {
      try {
        activeScreening = await api.post('/api/b2b/screenings', {
          job_id: jobId,
          candidate_id: candidateId,
          questions: [],
          expires_in_days: 30,
          language: screeningLanguage,
        })
        setScreening(activeScreening)
      } catch (err) {
        const message = err instanceof APIError ? extractErrorMessage(err.data) : err.message
        setAiError(message || 'Could not initialize screening.')
        return
      }
    }

    try {
      // Stage A — plan labels.
      setGenStage('planning')
      const planData = await api.post(`/api/b2b/screenings/${activeScreening.id}/plan-questions`, {
        categories: aiCategories,
        target_count: aiCount,
        include_fit_recommendations: true,
      })
      const labels = planData.labels || []
      if (labels.length === 0) {
        setAiError('No questions could be planned. Try different categories.')
        return
      }

      // Stage B — write full questions.
      setGenStage('writing')
      const writeData = await api.post(`/api/b2b/screenings/${activeScreening.id}/write-questions`, {
        labels,
      })
      const generated = (writeData.questions || []).map((q) => ({
        id: tempId(),
        text: q.question,
        category: q.category || null,
        rationale: q.rationale || null,
        expected_signal: q.expected_signal || null,
      }))
      if (generated.length > 0) {
        setQuestions(generated)
        // Backend snapshots questions_generated_language on the screening;
        // mirror it locally so the mismatch banner is accurate without a
        // refetch.
        setScreening((s) => s ? { ...s, questions_generated_language: screeningLanguage } : s)
      } else {
        setAiError('No questions returned. Try again.')
      }
    } catch (err) {
      const message = err instanceof APIError ? extractErrorMessage(err.data) : err.message
      setAiError(message || 'AI generation failed.')
    } finally {
      setGenStage(null)
    }
  }

  const handleSave = async () => {
    setError('')
    setQuotaError('')
    const cleaned = questions
      .map((q, idx) => ({
        text: q.text.trim(),
        position: idx,
        category: q.category || null,
        rationale: q.rationale || null,
        expected_signal: q.expected_signal || null,
      }))
      .filter((q) => q.text)
    if (cleaned.length === 0) {
      setError('Add at least one question.')
      return
    }

    setSaving(true)
    try {
      let saved
      if (screening?.id) {
        saved = await api.patch(`/api/b2b/screenings/${screening.id}/questions`, { questions: cleaned })
      } else {
        saved = await api.post('/api/b2b/screenings', {
          job_id: jobId,
          candidate_id: candidateId,
          questions: cleaned,
          expires_in_days: 30,
          language: screeningLanguage,
        })
      }
      setScreening(saved)
      setQuestions(saved.questions.map((q) => ({ id: q.id, text: q.text })))
      // Refresh quota in store so Dashboard widget reflects the new usage.
      loadSubscription()
    } catch (err) {
      // Quota exceeded — backend returns 402 Payment Required.
      if (err instanceof APIError && err.status === 402) {
        setQuotaError(extractErrorMessage(err.data) || 'Screening quota exceeded. Upgrade plan.')
      } else {
        const message = err instanceof APIError ? extractErrorMessage(err.data) : err.message
        setError(message || 'Could not save screening.')
      }
    } finally {
      setSaving(false)
    }
  }

  // Same domain after merge — candidate page lives at jobskills.work/screen/:token.
  const screeningUrl = screening?.token
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/screen/${screening.token}`
    : ''

  const handleCopy = async () => {
    if (!screeningUrl) return
    try {
      await navigator.clipboard.writeText(screeningUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked */
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-3xl mx-auto">
          <p className="text-sm text-ink-3">Loading…</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl mx-auto">
        <Link
          to={`/b2b/jobs/${jobId}/candidates/${candidateId}`}
          className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to candidate
        </Link>

        <h1 className="mt-4 text-2xl md:text-3xl font-bold text-ink-1">Screening questions</h1>
        <p className="text-sm text-ink-3 mt-1">
          {isCompleted ? (
            <>This screening has been completed by <strong>{candidate?.name}</strong>.</>
          ) : (
            <>Write the questions you want <strong>{candidate?.name}</strong> to answer for <strong>{job?.title}</strong>.</>
          )}
        </p>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {quotaError && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 mt-0.5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-900">Screening quota reached</p>
                <p className="mt-1 text-sm text-amber-800">{quotaError}</p>
                <Link
                  to="/b2b/pricing"
                  className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-4 py-2 rounded-xl text-sm transition"
                >
                  Upgrade plan
                </Link>
              </div>
            </div>
          </div>
        )}

        {!isCompleted
          && screening?.questions_generated_language
          && screening.questions_generated_language !== screeningLanguage
          && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-900">
                Your saved questions are in <strong>{languageLabel(screening.questions_generated_language)}</strong>,
                but the screening language is now <strong>{languageLabel(screeningLanguage)}</strong>.
                The candidate answers in the question's language, but the AI summary and BARS report will be
                generated in <strong>{languageLabel(screeningLanguage)}</strong> — regenerate the questions if
                you want everything in one language.
              </p>
            </div>
          )}

        {!isCompleted && (
          <div className="mt-8 bg-white rounded-3xl shadow-card border border-black/5 p-5">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-4 h-4 text-accent" />
              <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Generate with AI</p>
            </div>
            <p className="text-sm text-ink-2 mb-4">
              The AI tailors each question to this candidate's CV and the job. Pick categories, then generate. You'll be able to edit before saving.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {QUESTION_CATEGORIES.map((cat) => {
                const on = aiCategories.includes(cat.id)
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                      on
                        ? 'bg-accent text-white border-accent'
                        : 'bg-white text-ink-2 border-black/10 hover:border-accent/50'
                    }`}
                  >
                    {cat.label}
                  </button>
                )
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-3">
                Language
                <select
                  value={screeningLanguage}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  className="ml-2 px-2 py-1 rounded-lg border border-black/10 text-sm font-semibold text-ink-1 focus:outline-none focus:border-accent disabled:opacity-50"
                  disabled={aiGenerating || languageSaving || isCompleted}
                >
                  {SCREENING_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-bold uppercase tracking-wider text-ink-3">
                Count
                <input
                  type="number"
                  min={3}
                  max={15}
                  value={aiCount}
                  onChange={(e) => setAiCount(Math.max(3, Math.min(15, Number(e.target.value) || 8)))}
                  className="ml-2 w-16 px-2 py-1 rounded-lg border border-black/10 text-sm font-semibold text-ink-1 focus:outline-none focus:border-accent"
                  disabled={aiGenerating}
                />
              </label>
              <button
                type="button"
                onClick={handleGenerateAI}
                disabled={aiGenerating || aiCategories.length === 0}
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-4 py-2 rounded-xl transition disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" />
                {aiGenerating
                  ? (genStage === 'planning' ? 'Planning…' : 'Writing…')
                  : (questions.length > 1 || (questions[0]?.text || '') ? 'Regenerate' : 'Generate questions')}
              </button>
              {aiGenerating && (
                <span className="text-xs text-ink-3">
                  {genStage === 'planning' ? 'Planning questions' : 'Writing questions'}
                  {' · '}{Math.floor(genElapsed / 60)}:{String(genElapsed % 60).padStart(2, '0')}
                  {' · existing questions will be replaced'}
                </span>
              )}
            </div>
            {aiError && (
              <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
                {aiError}
              </div>
            )}
          </div>
        )}

        {!isCompleted && (
          <div className="mt-6 space-y-3">
            {questions.map((q, idx) => (
              <div key={q.id} className="bg-white rounded-2xl shadow-card border border-black/5 p-4">
                <div className="flex items-start gap-2">
                  <span className="mt-2 text-[11px] font-bold text-ink-3 uppercase tracking-wider">Q{idx + 1}</span>
                  <div className="flex-1">
                    {q.category && (
                      <div className="mb-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-accent bg-accent/5 px-2 py-0.5 rounded-full">
                        <Sparkles className="w-3 h-3" />
                        {q.category.replace('_', ' ')}
                      </div>
                    )}
                    <textarea
                      placeholder="e.g., Walk me through a backend system you designed end-to-end."
                      className="w-full text-[14px] outline-none bg-transparent resize-none min-h-[64px] placeholder:text-ink-3"
                      value={q.text}
                      onChange={(e) => updateQuestion(q.id, e.target.value)}
                      disabled={saving}
                      rows={2}
                    />
                    {q.expected_signal && (
                      <p className="mt-1 text-[12px] text-ink-3 italic">
                        <span className="font-semibold text-ink-2 not-italic">Strong answer signals:</span>{' '}
                        {q.expected_signal}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuestion(q.id)}
                    disabled={questions.length <= 1 || saving}
                    className="text-ink-3 hover:text-red-500 p-1.5 rounded hover:bg-red-50 transition disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-3"
                    aria-label="Remove question"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addQuestion}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-black/15 rounded-2xl py-3 text-sm font-semibold text-ink-2 hover:bg-canvas hover:text-ink-1 transition"
            >
              <Plus className="w-4 h-4" />
              Add question
            </button>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition disabled:opacity-50"
              >
                {saving ? 'Saving…' : screening?.id ? 'Update questions' : 'Generate link'}
              </button>
            </div>
          </div>
        )}

        {screening?.token && (
          <div className="mt-10 bg-white rounded-3xl shadow-card p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-ink-3">Candidate link</h2>
            <p className="mt-2 text-sm text-ink-2">
              Send this link to <strong>{candidate?.name}</strong>. You'll see their answers here once they submit.
              Link expires {fmtDate(screening.expires_at)}.
            </p>

            <div className="mt-4 flex items-center gap-2 bg-canvas rounded-2xl px-4 py-3">
              <code className="flex-1 text-[12px] text-ink-2 truncate">{screeningUrl}</code>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-1 hover:bg-white px-3 py-1.5 rounded-xl transition"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            {isCompleted && (
              <p className="mt-4 text-sm text-green-700 bg-green-50 rounded-xl px-4 py-2.5 inline-block">
                ✓ Submitted on {fmtDate(screening.completed_at)}. View answers on the candidate page.
              </p>
            )}
          </div>
        )}
      </div>
    </AppShell>
  )
}

function tempId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `tmp-${Math.random().toString(36).slice(2)}-${Date.now()}`
}

function fmtDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}
